import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BoltIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Layout from '../components/layout/Layout';
import { webhooksAPI, leadStagesAPI, leadsAPI } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER_BASE = API_BASE.replace('/api', '');

// ── CRM field options visible to users ──────────────────────────────────────
const CRM_FIELD_OPTIONS = [
  { value: '', label: '— Select CRM field —' },
  { label: 'Contact Info', options: [
    { value: 'firstName', label: 'First Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'email', label: 'Email Address' },
    { value: 'phone', label: 'Phone Number' },
    { value: 'companyName', label: 'Company Name' },
    { value: 'city', label: 'City' },
    { value: 'state', label: 'State / Province' },
    { value: 'country', label: 'Country' },
  ]},
  { label: 'Lead Fields', options: [
    { value: 'lead.notes', label: 'Lead Notes' },
    { value: 'lead.value', label: 'Lead Value (number)' },
    { value: 'lead.source', label: 'Source' },
    { value: 'lead.priority', label: 'Priority (low/medium/high)' },
  ]},
  { label: 'Custom Fields (prefix: custom.)', options: [
    { value: 'custom.__manual__', label: '+ Type a custom field name manually' },
  ]},
];

const SOURCE_OPTIONS = ['Website', 'Referral', 'Social Media', 'Email Campaign', 'Cold Call', 'Trade Show', 'Direct Mail', 'Online Ads', 'Partnership', 'Other'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high'];

// ── Step guide content ───────────────────────────────────────────────────────
const GUIDE_STEPS = [
  {
    step: 1,
    title: 'Create a Make.com Scenario',
    desc: 'Log in to Make.com and click "Create a new scenario". Search for "Facebook Lead Ads" in the app list and select it as your trigger.',
    icon: '①'
  },
  {
    step: 2,
    title: 'Connect Facebook Lead Ads',
    desc: 'Authenticate with your Facebook account and select the Facebook Page and Lead Form you want to monitor. Test the trigger to pull sample data.',
    icon: '②'
  },
  {
    step: 3,
    title: 'Add an HTTP module',
    desc: 'Add a new module after the trigger. Search for "HTTP" → "Make a request". This will send the lead data to your CRM.',
    icon: '③'
  },
  {
    step: 4,
    title: 'Configure the HTTP module',
    desc: 'Set Method to POST. Paste your Webhook URL (shown above) into the URL field. Set Body type to "Raw" and Content-Type to "application/json".',
    icon: '④'
  },
  {
    step: 5,
    title: 'Map the fields in the request body',
    desc: 'Build a JSON body using the Facebook fields. Use the mapping table on this page to know which Facebook field names to use (left column). Example: { "full_name": "{{1.full_name}}", "email": "{{1.email}}" }',
    icon: '⑤'
  },
  {
    step: 6,
    title: 'Turn on and test',
    desc: 'Click "Run once" in Make.com to test with a real or sample lead. Check the Logs section below to confirm a lead was created in CRM.',
    icon: '⑥'
  }
];

// ── Component ────────────────────────────────────────────────────────────────
export default function WebhookIntegration() {
  const [webhook, setWebhook] = useState(null);
  const [loadingWebhook, setLoadingWebhook] = useState(true);
  const [saving, setSaving] = useState(false);

  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [mappings, setMappings] = useState([{ facebookField: '', crmField: '' }]);
  const [defaults, setDefaults] = useState({ stage: '', assignedTo: '', source: 'Social Media', priority: 'medium' });
  const [webhookName, setWebhookName] = useState('Facebook Lead Webhook');
  const [isActive, setIsActive] = useState(true);

  const [copied, setCopied] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [customFieldInputs, setCustomFieldInputs] = useState({});

  const webhookUrl = webhook ? `${SERVER_BASE}/api/webhooks/receive/${webhook.token}` : '';

  // ── Load initial data ────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoadingWebhook(true);
    try {
      const [wRes, sRes, uRes] = await Promise.all([
        webhooksAPI.get(),
        leadStagesAPI.getAll(),
        leadsAPI.getAssignableUsers()
      ]);

      const wh = wRes.data.webhook;
      setWebhook(wh);

      if (wh) {
        setWebhookName(wh.name || 'Facebook Lead Webhook');
        setIsActive(wh.isActive);
        setMappings(wh.fieldMappings && wh.fieldMappings.length > 0
          ? wh.fieldMappings
          : [{ facebookField: '', crmField: '' }]);
        setDefaults({
          stage: wh.defaultValues?.stage?._id || wh.defaultValues?.stage || '',
          assignedTo: wh.defaultValues?.assignedTo?._id || wh.defaultValues?.assignedTo || '',
          source: wh.defaultValues?.source || 'Social Media',
          priority: wh.defaultValues?.priority || 'medium'
        });
      }

      setStages(sRes.data.leadStages || []);
      setUsers(uRes.data.users || []);
    } catch {
      toast.error('Failed to load webhook configuration');
    } finally {
      setLoadingWebhook(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadLogs = useCallback(async () => {
    if (!webhook?._id) return;
    setLoadingLogs(true);
    try {
      const res = await webhooksAPI.getLogs(webhook._id);
      setLogs(res.data.logs || []);
    } catch {
      toast.error('Failed to load logs');
    } finally {
      setLoadingLogs(false);
    }
  }, [webhook?._id]);

  useEffect(() => { if (webhook?._id) loadLogs(); }, [webhook?._id, loadLogs]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setSaving(true);
    try {
      const cleanMappings = mappings.filter(m => m.facebookField.trim() && m.crmField.trim());
      const res = await webhooksAPI.create({
        name: webhookName,
        fieldMappings: cleanMappings,
        defaultValues: defaults
      });
      setWebhook(res.data.webhook);
      toast.success('Webhook created!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!webhook) return;
    setSaving(true);
    try {
      const cleanMappings = mappings.filter(m => m.facebookField.trim() && m.crmField.trim());
      const res = await webhooksAPI.update(webhook._id, {
        name: webhookName,
        fieldMappings: cleanMappings,
        defaultValues: defaults,
        isActive
      });
      setWebhook(res.data.webhook);
      toast.success('Webhook settings saved!');
    } catch {
      toast.error('Failed to save webhook settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!webhook || !window.confirm('Delete this webhook? All incoming leads from this URL will stop working.')) return;
    try {
      await webhooksAPI.delete(webhook._id);
      setWebhook(null);
      setMappings([{ facebookField: '', crmField: '' }]);
      setDefaults({ stage: '', assignedTo: '', source: 'Social Media', priority: 'medium' });
      toast.success('Webhook deleted');
    } catch {
      toast.error('Failed to delete webhook');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(webhookUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const addMapping = () => setMappings(prev => [...prev, { facebookField: '', crmField: '' }]);

  const updateMapping = (idx, field, value) => {
    setMappings(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const removeMapping = (idx) => {
    setMappings(prev => prev.filter((_, i) => i !== idx));
  };

  if (loadingWebhook) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

        {/* ── Page Header ── */}
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
            <BoltIcon className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Facebook Lead Ads Integration</h1>
            <p className="text-sm text-gray-500">Connect your Facebook Lead Form via Make.com to automatically create CRM leads</p>
          </div>
        </div>

        {/* ── No webhook yet ── */}
        {!webhook && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center"
          >
            <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
              <LinkIcon className="h-8 w-8 text-blue-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">No webhook configured yet</h2>
            <p className="text-sm text-gray-500 mb-6">
              Create a webhook to receive leads from Facebook Lead Ads via Make.com.
              A unique secure URL will be generated for your company.
            </p>
            <div className="max-w-sm mx-auto mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Webhook name</label>
              <input
                type="text"
                value={webhookName}
                onChange={e => setWebhookName(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Facebook Lead Webhook"
              />
            </div>
            <button
              onClick={handleCreate}
              disabled={saving}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md transition-all"
            >
              {saving ? <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" /> : <BoltIcon className="h-4 w-4 mr-2" />}
              Create Webhook
            </button>
          </motion.div>
        )}

        {/* ── Webhook configured ── */}
        {webhook && (
          <>
            {/* URL Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-800 flex items-center space-x-2">
                  <LinkIcon className="h-5 w-5 text-blue-500" />
                  <span>Your Webhook URL</span>
                </h2>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => { setIsActive(v => !v); }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-gray-300'}`}
                    title={isActive ? 'Disable webhook' : 'Enable webhook'}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <code className="flex-1 text-sm text-gray-700 break-all font-mono">{webhookUrl}</code>
                <button
                  onClick={handleCopy}
                  className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Copy URL"
                >
                  {copied
                    ? <CheckIcon className="h-4 w-4 text-green-600" />
                    : <ClipboardDocumentIcon className="h-4 w-4 text-gray-500" />}
                </button>
              </div>

              <p className="mt-2 text-xs text-gray-500 flex items-center space-x-1">
                <InformationCircleIcon className="h-4 w-4 flex-shrink-0" />
                <span>Paste this URL as the HTTP endpoint in Make.com. This receives POST requests with your lead data.</span>
              </p>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Webhook name</label>
                <input
                  type="text"
                  value={webhookName}
                  onChange={e => setWebhookName(e.target.value)}
                  className="w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Field Mapping */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-gray-800">Field Mapping</h2>
                <button
                  onClick={addMapping}
                  className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <PlusIcon className="h-3.5 w-3.5 mr-1" />
                  Add Row
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Map the field names coming from Facebook (left) to CRM fields (right).
                Facebook field names must match exactly what Make.com sends in the JSON body.
              </p>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
                  <span>Facebook Field Name</span>
                  <span>CRM Field</span>
                </div>

                {mappings.map((mapping, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-3 items-start">
                    <input
                      type="text"
                      placeholder="e.g. full_name"
                      value={mapping.facebookField}
                      onChange={e => updateMapping(idx, 'facebookField', e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    />
                    <div className="flex items-center space-x-2">
                      {mapping.crmField.startsWith('custom.') && !mapping.crmField.endsWith('__manual__') ? (
                        <div className="flex-1 flex items-center border border-blue-300 bg-blue-50 rounded-lg px-3 py-2">
                          <span className="text-xs text-blue-700 font-mono">custom.</span>
                          <input
                            type="text"
                            placeholder="fieldName"
                            value={mapping.crmField.slice(7)}
                            onChange={e => updateMapping(idx, 'crmField', `custom.${e.target.value}`)}
                            className="flex-1 bg-transparent text-sm text-blue-800 focus:outline-none font-mono"
                          />
                        </div>
                      ) : (
                        <select
                          value={mapping.crmField === 'custom.__manual__' ? '' : mapping.crmField}
                          onChange={e => {
                            if (e.target.value === 'custom.__manual__') {
                              updateMapping(idx, 'crmField', 'custom.');
                            } else {
                              updateMapping(idx, 'crmField', e.target.value);
                            }
                          }}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {CRM_FIELD_OPTIONS.map(opt =>
                            opt.options ? (
                              <optgroup key={opt.label} label={opt.label}>
                                {opt.options.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </optgroup>
                            ) : (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            )
                          )}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={() => removeMapping(idx)}
                        disabled={mappings.length === 1}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-700 font-medium">
                  Common Facebook Lead Ads field names: <code className="font-mono">full_name</code>, <code className="font-mono">email</code>, <code className="font-mono">phone_number</code>, <code className="font-mono">city</code>, <code className="font-mono">state</code>, <code className="font-mono">country</code>
                </p>
              </div>
            </div>

            {/* Default Values */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-800 mb-4">Default Values for New Leads</h2>
              <p className="text-xs text-gray-500 mb-4">
                These values are applied when creating a lead if not mapped from the incoming data.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Stage</label>
                  <select
                    value={defaults.stage}
                    onChange={e => setDefaults(d => ({ ...d, stage: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Auto (first stage)</option>
                    {stages.map(s => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <select
                    value={defaults.assignedTo}
                    onChange={e => setDefaults(d => ({ ...d, assignedTo: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Unassigned</option>
                    {users.map(u => (
                      <option key={u._id} value={u._id}>{u.name} ({u.role})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select
                    value={defaults.source}
                    onChange={e => setDefaults(d => ({ ...d, source: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={defaults.priority}
                    onChange={e => setDefaults(d => ({ ...d, priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Save button */}
            <div className="flex justify-between items-center">
              <button
                onClick={handleDelete}
                className="inline-flex items-center px-4 py-2 text-sm text-red-600 border border-red-300 rounded-xl hover:bg-red-50 transition-colors"
              >
                <TrashIcon className="h-4 w-4 mr-1.5" />
                Delete Webhook
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md transition-all"
              >
                {saving ? <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" /> : <CheckIcon className="h-4 w-4 mr-2" />}
                Save Settings
              </button>
            </div>
          </>
        )}

        {/* ── Make.com Setup Guide ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowGuide(v => !v)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <InformationCircleIcon className="h-5 w-5 text-indigo-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-800">Step-by-step: Make.com Setup Guide</h2>
            </div>
            {showGuide
              ? <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              : <ChevronDownIcon className="h-5 w-5 text-gray-500" />}
          </button>

          <AnimatePresence>
            {showGuide && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6 space-y-4 border-t border-gray-100">
                  {GUIDE_STEPS.map(step => (
                    <div key={step.step} className="flex items-start space-x-4 pt-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-lg font-bold text-indigo-700">
                        {step.step}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800 mb-1">{step.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}

                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium mb-2">Sample JSON body for Make.com HTTP module:</p>
                    <pre className="text-xs text-blue-700 font-mono bg-white rounded-lg p-3 border border-blue-100 overflow-x-auto">{`{
  "full_name": "{{1.full_name}}",
  "email": "{{1.email}}",
  "phone_number": "{{1.phone_number}}",
  "city": "{{1.city}}",
  "country": "{{1.country}}"
}`}
                    </pre>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Logs ── */}
        {webhook && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-800">Recent Activity (last 50)</h2>
              <button
                onClick={loadLogs}
                disabled={loadingLogs}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Refresh logs"
              >
                <ArrowPathIcon className={`h-4 w-4 text-gray-500 ${loadingLogs ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {logs.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <BoltIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No webhook calls yet. Logs will appear here after Make.com sends a request.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
                      <th className="pb-2 text-left font-semibold">Time</th>
                      <th className="pb-2 text-left font-semibold">Status</th>
                      <th className="pb-2 text-left font-semibold">Lead #</th>
                      <th className="pb-2 text-left font-semibold">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {logs.map((log, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="py-2 pr-4">
                          {log.status === 'success' ? (
                            <span className="inline-flex items-center space-x-1 text-green-700">
                              <CheckCircleIcon className="h-4 w-4" />
                              <span>Success</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center space-x-1 text-red-600">
                              <XCircleIcon className="h-4 w-4" />
                              <span>Error</span>
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-gray-700 font-mono">
                          {log.leadId?.leadno || (log.leadId ? '—' : '—')}
                        </td>
                        <td className="py-2 text-red-600 text-xs max-w-xs truncate">
                          {log.error || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
